<template>
  <div
    class="be-landing-navbar w-100 position-fixed top-0 z-index-1040"
    :class="{ 'is-top': isAtTop }">
    <div class="ga-container fluid py-2">
      <div class="d-flex align-items-center justify-content-between w-100">
        <!-- LOGO -->
        <div class="be-landing-navbar__brand d-flex align-items-center cursor-pointer" @click="goToHomepage">
          <img
            v-if="merchantLogo"
            :src="merchantLogo"
            :alt="getMerchant.name"
            class="be-landing-navbar__logo" />
          <span v-else class="body-1-4 font-bold">{{ getMerchant.name }}</span>
        </div>

        <!-- RIGHT -->
        <div class="be-landing-navbar__right d-flex align-items-center">
          <!-- cart -->
          <router-link
            :to="`/${getMerchant.code}/cart`"
            class="d-flex align-items-center px-2 px-md-3 py-2 be-landing-navbar__item be-landing-navbar__cart text-decoration-none position-relative">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13 5.4 5M7 13l-2.3 4.6c-.3.7.2 1.4.9 1.4H17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
              <circle cx="9" cy="20" r="1.4" fill="currentColor" />
              <circle cx="17" cy="20" r="1.4" fill="currentColor" />
            </svg>
            <span class="body-2-4 ml-2 be-landing-navbar__cart-label">Cart</span>
            <span
              v-if="cartCount > 0"
              class="be-landing-navbar__badge"
              :class="{ 'be-pop': isBadgePulsing }">{{ cartCount }}</span>
          </router-link>

          <!-- currency -->
          <div
            class="d-flex align-items-center cursor-pointer px-2 px-md-3 py-2 be-landing-navbar__item be-landing-navbar__currency"
            @click="toggleLanguagePopup(true)">
            <span class="be-landing-navbar__dot" />
            <span class="body-2-4 mx-2 be-landing-navbar__currency-code">{{ getCurrency.code }}</span>
            <svg class="be-landing-navbar__currency-chevron" height="7" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.53 8c.19 0 .38-.08.5-.22l5.82-5.96a.63.63 0 0 0 .2-.47c0-.39-.29-.69-.68-.69-.19 0-.36.07-.49.19L6.12 6.73h.8L1.17.85A.68.68 0 0 0 .68.65C.29.65 0 .95 0 1.34c0 .19.08.36.2.5l5.82 5.94c.14.14.31.22.51.22Z" fill="currentColor" />
            </svg>
          </div>

          <!-- ACCOUNT (mobile) -->
          <button type="button" class="be-landing-navbar__icon be-landing-navbar__account-toggle" :aria-label="$t('general.account')" @click="toggleAccountMenu(!isAccountOpen)">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="8" r="3.5" stroke="currentColor" stroke-width="1.6" />
              <path d="M5 19c1.5-3.5 4.5-5 7-5s5.5 1.5 7 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </button>

          <div v-if="isAccountOpen" class="be-landing-navbar__backdrop" @click="toggleAccountMenu(false)"></div>

          <!-- inline on desktop, collapsed behind the account icon on mobile -->
          <div class="be-landing-navbar__account-menu" :class="{ 'is-open': isAccountOpen }">
            <template v-if="isLoggedIn">
              <router-link
                :to="`/${getMerchant.code}/account`"
                class="d-flex align-items-center px-3 py-2 be-landing-navbar__item be-landing-navbar__auth text-decoration-none">
                <span class="body-2-4">{{ accountName }}</span>
              </router-link>
            </template>
            <template v-else>
              <button
                type="button"
                class="body-2-4 px-2 px-md-3 py-2 be-landing-navbar__item be-landing-navbar__auth be-landing-navbar__auth-btn"
                @click="openAuth('login')">
                {{ $t('general.login-now') }}
              </button>
              <button
                type="button"
                class="be-landing-navbar__register button-primary px-3 px-md-4 py-2 text-white"
                @click="openAuth('register')">
                {{ $t('general.register-now') }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <GeneralLanguageCurrency
      :showPopup="showLanguagePopup"
      @togglePopup="toggleLanguagePopup" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import GeneralLanguageCurrency from '@/components/general/GeneralLanguageCurrency.vue';

/* matches the bePop keyframe length in be-v3-motion.css */
const BADGE_PULSE_MS = 260;
/* on mobile the bar floats over the hero image until the page scrolls past it */
const NAVBAR_SOLID_OFFSET_PX = 24;

export default {
  name: 'BeLandingNavbar',
  components: {
    GeneralLanguageCurrency,
  },
  data() {
    return {
      showLanguagePopup: false,
      isBadgePulsing: false,
      isAccountOpen: false,
      isAtTop: true,
    };
  },
  computed: {
    ...mapGetters([
      'getMerchant',
      'getCurrency',
      'isLoggedIn',
      'getCurrentUser',
      'getBeCartRoom',
      'getBeCartAddtService',
      'getOutletTrxProducts',
      'getActivityTrxProducts',
      'getVoucherOrder',
    ]),
    merchantLogo() {
      const merchant = this.getMerchant;
      if (merchant.search_room_is_show_merchant_image === false) return '';
      return merchant.image_url || '';
    },
    accountName() {
      const user = this.getCurrentUser;
      return (user && user.first_name) || this.$t('general.account');
    },
    voucherProductCount() {
      const voucherCart = (this.getVoucherOrder && this.getVoucherOrder.cart) || [];
      return voucherCart.reduce((total, pos) => total + ((pos.product && pos.product.length) || 0), 0);
    },
    cartCount() {
      const roomCount = (this.getBeCartRoom && this.getBeCartRoom.length) || 0;
      const addtServiceCount = (this.getBeCartAddtService && this.getBeCartAddtService.length) || 0;
      const outletCount = (this.getOutletTrxProducts && this.getOutletTrxProducts.length) || 0;
      const activityCount = (this.getActivityTrxProducts && this.getActivityTrxProducts.length) || 0;
      return roomCount + addtServiceCount + outletCount + activityCount + this.voucherProductCount;
    },
  },
  watch: {
    cartCount(count, previousCount) {
      if (count <= previousCount) return;
      this.pulseBadge();
    },
  },
  mounted() {
    window.addEventListener('scroll', this.syncScrollState, { passive: true });
    this.syncScrollState();
  },
  beforeDestroy() {
    clearTimeout(this.badgePulseTimer);
    window.removeEventListener('scroll', this.syncScrollState);
  },
  methods: {
    syncScrollState() {
      this.isAtTop = window.pageYOffset < NAVBAR_SOLID_OFFSET_PX;
    },
    toggleAccountMenu(isOpen = false) {
      this.isAccountOpen = isOpen;
    },
    openAuth(view) {
      this.toggleAccountMenu(false);
      this.$openAuthModal({ view: view });
    },
    pulseBadge() {
      clearTimeout(this.badgePulseTimer);
      this.isBadgePulsing = false;
      // let the class drop for a frame so re-adding it restarts the keyframes
      this.$nextTick(() => {
        this.isBadgePulsing = true;
        this.badgePulseTimer = setTimeout(() => {
          this.isBadgePulsing = false;
        }, BADGE_PULSE_MS);
      });
    },
    toggleLanguagePopup(show = false) {
      this.showLanguagePopup = show;
      if (show) this.toggleAccountMenu(false);
    },
    goToHomepage() {
      const customHomepage = this.getMerchant.search_room_custom_homepage_url;
      if (this.$isNotEmptyV2(customHomepage)) {
        this.$openLink(customHomepage);
        return;
      }
      this.$router.push(`/${this.getMerchant.code}`);
    },
  },
};
</script>

<style scoped>
.be-landing-navbar {
  min-height: 78px;
  display: flex;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #dee2e6;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
.be-landing-navbar__right {
  position: relative;
}
/* transparent wrapper on desktop so its items stay siblings of the cart and keep the
   original bar order; on mobile it becomes the dropdown behind the account icon */
.be-landing-navbar__account-menu {
  display: contents;
}
.be-landing-navbar__currency {
  order: 1;
}
.be-landing-navbar__cart {
  order: 2;
}
.be-landing-navbar__auth {
  order: 3;
  margin-left: 4px;
}
.be-landing-navbar__register {
  order: 4;
  margin-left: 8px;
}
/* the auth entry points are buttons now (they open the popup, not a page) */
.be-landing-navbar__auth-btn {
  background: none;
}
.be-landing-navbar__auth-btn,
.be-landing-navbar__register {
  border: none;
  cursor: pointer;
  font: inherit;
}
/* mobile-only controls */
.be-landing-navbar__icon {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  color: #222;
  border-radius: 50%;
  cursor: pointer;
}
.be-landing-navbar__logo {
  max-height: 52px;
  max-width: 200px;
  object-fit: contain;
}
.be-landing-navbar__item {
  border-radius: var(--be-v3-radius, 6px);
  color: #222;
  transition: background-color 0.15s ease;
}
.be-landing-navbar__item:hover {
  background-color: #f5f6f8;
}
.be-landing-navbar__dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: linear-gradient(180deg, #e70000 50%, #fff 50%);
  border: 1px solid #eee;
  display: inline-block;
}
.be-landing-navbar__register {
  border-radius: var(--be-v3-button-radius, 8px);
  font-weight: 500;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
}
.be-landing-navbar__badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--merchant-primary-color, #79a33a);
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
}
@media screen and (max-width: 767.98px) {
  .be-landing-navbar {
    min-height: 64px;
    border-bottom: none;
  }
  /* floats over the hero gallery until the page scrolls past it — the scrim keeps
     the white controls legible against a bright photo */
  .be-landing-navbar.is-top {
    background: transparent;
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.38), rgba(0, 0, 0, 0));
  }
  .be-landing-navbar.is-top .be-landing-navbar__icon,
  .be-landing-navbar.is-top .be-landing-navbar__item {
    color: #fff;
  }
  .be-landing-navbar.is-top .be-landing-navbar__item:hover {
    background-color: rgba(255, 255, 255, 0.18);
  }
  .be-landing-navbar__cart-label {
    display: none;
  }
  .be-landing-navbar__logo {
    max-height: 28px;
    max-width: 120px;
  }
  .be-landing-navbar__icon {
    display: inline-flex;
  }
  /* collapses to a plain icon button; the code and chevron only make sense with room to breathe */
  .be-landing-navbar__currency {
    width: 40px;
    height: 40px;
    padding: 0;
    justify-content: center;
  }
  .be-landing-navbar__currency-code,
  .be-landing-navbar__currency-chevron {
    display: none;
  }
  .be-landing-navbar__backdrop {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1039;
  }
  /* the items that no longer fit the bar collapse behind the account icon */
  .be-landing-navbar__account-menu {
    display: none;
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    z-index: 1041;
    min-width: 190px;
    flex-direction: column;
    align-items: stretch;
    background: #fff;
    border-radius: var(--be-v3-radius, 10px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
    padding: 8px;
  }
  .be-landing-navbar__account-menu.is-open {
    display: flex;
  }
  .be-landing-navbar__cart {
    order: 0;
  }
  .be-landing-navbar__account-toggle {
    order: 2;
  }
  .be-landing-navbar__auth,
  .be-landing-navbar__register {
    margin-left: 0;
    margin-top: 4px;
  }
  .be-landing-navbar__account-menu .be-landing-navbar__item {
    color: #222;
  }
  .be-landing-navbar__register {
    justify-content: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeLandingNavbar.vue